import { Component, ContentChild, Directive, effect, HostListener, input, OnDestroy, TemplateRef } from '@angular/core';
import { Reference } from '../../models/reference';
import { TranslateModule } from '@ngx-translate/core';
import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { CommonExplainModule } from '../../common-explain/common-explain.module';
import { NgbModal, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { MatIcon } from '@angular/material/icon';
import { ApiUserService } from '../../shared/services/api/user/api-user.service';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { ModalJobDescriptionComponent } from '../modal-job-description/modal-job-description.component';
import { ModalBusinessEmailComponent } from '../modal-business-email/modal-business-email.component';
import { BusinessEmailService } from '../../shared/services/business-email.service';
import { User } from '../../models/user';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Directive({ selector: '[references-placeholder]', standalone: true })
export class ReferencesPlaceholderDirective {}

@Directive({ selector: '[references-loading]', standalone: true })
export class ReferencesLoadingDirective {}

@Directive({ selector: '[references-empty]', standalone: true })
export class ReferencesEmptyDirective {}

@Directive({ selector: '[references-error]', standalone: true })
export class ReferencesErrorDirective {}

@Directive({ selector: '[references-custom-actions]', standalone: true })
export class ReferencesCustomActionsDirective {}

interface DocumentContext {
  docId: string;
  digestResultsId?: string;
  contentType: 'admin_doc' | 'article';
}

@Component({
  selector: 'references-panel',
  standalone: true,
  templateUrl: './references-panel.component.html',
  imports: [TranslateModule, NgTemplateOutlet, CommonExplainModule, NgbTooltip, MatIcon, AsyncPipe, NgClass],
  styleUrls: ['./references-panel.component.scss'],
})
export class ReferencesPanelComponent<T extends Reference> implements OnDestroy {
  references = input<T[] | null | undefined | Promise<T[] | null | undefined>>(undefined);
  selectable = input(false);
  ready = input<undefined | boolean>(undefined);
  context = input<DocumentContext | undefined>(undefined);

  _references?: T[] | null;
  loading = false;
  generating = false;

  selected?: T;
  user?: Promise<User | undefined>;
  userRoleSet = false;
  hoveredReferenceId$ = new BehaviorSubject<string | undefined>(undefined);
  referencesStyle!: SafeHtml;

  @ContentChild(ReferencesPlaceholderDirective, { read: TemplateRef }) placeholderTemplate?: TemplateRef<any>;
  @ContentChild(ReferencesLoadingDirective, { read: TemplateRef }) loadingTemplate?: TemplateRef<any>;
  @ContentChild(ReferencesEmptyDirective, { read: TemplateRef }) emptyTemplate?: TemplateRef<any>;
  @ContentChild(ReferencesErrorDirective, { read: TemplateRef }) errorTemplate?: TemplateRef<any>;
  @ContentChild(ReferencesCustomActionsDirective, { read: TemplateRef }) customActionsTemplate?: TemplateRef<any>;

  constructor(
    private modalService: NgbModal,
    private apiUserService: ApiUserService,
    private businessEmailService: BusinessEmailService,
    private sanitizer: DomSanitizer
  ) {
    effect(() => {
      const references = this.references();
      if (references instanceof Promise) {
        this.loading = true;
        references
          .then(resolved => {
            this._references = resolved;
          })
          .catch(() => {
            this._references = null;
          })
          .finally(() => {
            this.loading = false;
          });
      } else {
        this._references = references;
      }
    });
    this.user = firstValueFrom(this.apiUserService.searchUserWithFields(['job_description']));
    this.hoveredReferenceId$.subscribe(value => this.updateReferencesStyle(value));
  }

  ngOnDestroy(): void {
    this.hoveredReferenceId$.complete();
  }

  onSelect(reference: T): void {
    if (this.selectable()) {
      if (this.selected !== reference) this.selected = reference;
      else this.selected = undefined;
    }
  }

  private updateReferencesStyle(key?: string) {
    // Angular does not permit style tags in the template, so we bypass it as it is not a security
    // issue in our case.
    const refClass = '.ref-id-' + (key ?? 'none');
    this.referencesStyle = this.sanitizer.bypassSecurityTrustHtml(
      `<style>
        ${refClass}, ${refClass} .ref-color {
          color: var(--primary-500) !important
        }
        ${refClass} .ref-color img {
          filter: var(--filter-primary-500)
        }
        ${refClass}.ref-background {
          background-color: var(--basic-200)
        }
      </style>`
    );
  }

  @HostListener('window:mouseover', ['$event.target'])
  onHover(target: HTMLElement) {
    const classes = target.closest('[class*="ref-id-"]')?.getAttribute('class') ?? '';
    const key = /^.*ref-id-(?<key>\d+).*/.exec(classes)?.groups?.['key'];
    this.hoveredReferenceId$.next(key);
  }

  async generateBusinessEmail() {
    const context = this.context();
    if (!context) return;
    this.generating = true;
    if (!(await this.user)?.jobDescription && !this.userRoleSet) {
      const role = await this.modalService.open(ModalJobDescriptionComponent, { centered: true }).result.catch(() => undefined);
      if (role) {
        await this.apiUserService.updateUser({ job_description: role });
        this.userRoleSet = true;
      } else {
        this.generating = false;
        return;
      }
    }
    const generate = () =>
      this.businessEmailService.generateBusinessEmail(
        context.contentType,
        context.docId,
        context.digestResultsId,
        this.selected ? { name: this.selected.name, position: this.selected.value ?? '' } : undefined
      );
    const generationModal = this.modalService.open(ModalBusinessEmailComponent, { centered: true });
    generationModal.componentInstance.generate = generate;
    const result = await generationModal.result.catch(() => undefined);
    if (result?.email_body) {
      window.open(`mailto:?body=${encodeURIComponent(result.email_body)}`, '_blank');
    }
    this.generating = false;
  }
}
